<script setup>
import { onMounted } from 'vue'
import { TabGroup, TabList, Tab } from '@headlessui/vue'
import { useWindowSize } from '@vueuse/core/index.cjs'
import { useBillingStore } from '@/stores/BillingStore'
import { useUserStore } from '@/stores/UserStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useRouter } from 'vue-router'
import { POSTHOG_EVENTS, UPGRADE_MODALS, QUICK_UPGRADE_MODAL_TYPES } from '@/constants'
import { safeCapture } from '@/helpers/posthog'
import NumberFlow from '@number-flow/vue'
import UpgradeButton from '@/components/UpgradePlans/UpgradeButton.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

const props = defineProps({
  showAnnualSaveBadge: { type: Boolean, default: false },
  pageType: { type: String, default: 'account-creation' },
})

const { width } = useWindowSize()

const billingStore = useBillingStore()
const userStore = useUserStore()
const configStore = useConfigStore()

const router = useRouter()

onMounted(() => {
  billingStore.initSelectedPlan()
})

const changePlanPeriod = index => {
  billingStore.getPlansPeriods.selectedIndex = index
  billingStore.getPlansPeriods.selected = billingStore.getPlansPeriods.options[index].value
}

const handleSelectPlan = plan => {
  if (billingStore.currentPlan?.id === plan.id) return 
  billingStore.selectedPlan = plan
}

const handleContinue = plan => {
  localStorage.setItem('plan', JSON.stringify({ ...plan, period: billingStore.getPlansPeriods.selected }))
  
  safeCapture(POSTHOG_EVENTS.ONBOARDING_STEP_PLAN_COMPLETED, {
    userId: userStore.user.id,
    planName: plan.name
  })

  router.push({ path: '/account-creation/payment', query: { period: billingStore.getPlansPeriods.selected, plan: plan.value } })
}

const handleUpgrade = plan => {
  const paywallType = props.pageType === 'account-creation'
    ? 'onboarding_upsell'
    : billingStore.paywallContext || 'upgrade_modal'

  billingStore.upgradeFeatureModal = true

  billingStore.upgradeFeatureModalData = {
    ...UPGRADE_MODALS[QUICK_UPGRADE_MODAL_TYPES.UPGRADE_PLAN],
    isPlanUpgradeModal: true,
    isUpgrade: (billingStore.currentPlan?.id ?? 0) < plan.id,
    paywallType,
    plan
  }
};
</script>
<template>
  <section 
    class="flex flex-col gap-y-7"
    :class="{ '!gap-y-10': pageType === 'upgrade-plans' }"
  >
    <section class="plan-period-tabs w-fit mx-auto flex flex-col gap-y-4 items-center">
      <TabGroup
        :selected-index="billingStore.getPlansPeriods.selectedIndex"
        @change="changePlanPeriod"
      >
        <TabList class="tab-list !bg-[#f8f8f8]">
          <Tab
            as="template"
            v-slot="{ selected }"
            v-for="option in billingStore.getPlansPeriods.options"
            :key="option.id"
          >
            <button
              class="tab !text-sm !font-semibold"
              :class="selected && 'selected shadow-sm'"
              v-text="option.label"
            />
          </Tab>
        </TabList>
      </TabGroup>

      <div v-if="!billingStore.getPlansPeriods.selectedIndex" class="text-[16px] text-gray-600 transition-all duration-300 ease-in-out">
        <span class="font-semibold text-tint-1">Save up to {{ billingStore.selectedPlan ? billingStore.selectedPlan.price.annual_save : currentPlan.price.annual_save }}%</span> on an annual subscription
      </div>
    </section>

    <section class="flex max-sm:flex-col items-stretch gap-3 flex-wrap justify-center">
      <div
        v-for="plan in billingStore.getPlans"
        :key="plan.value"
        class="flex-1 rounded-lg border flex flex-col sm:max-xl:min-w-[16rem] cursor-pointer"
        :class=" {
          'border-tint-1 ring-4 ring-tint-1/20': billingStore.selectedPlan?.value === plan.value,
          'bg-gray-100 cursor-default pointer-events-none': billingStore.currentPlan?.id != null && billingStore.currentPlan.id === plan.id && configStore.config?.subscription?.annual !== billingStore.getPlansPeriods.selectedIndex
        }"
        @click="handleSelectPlan(plan)"
      >
        <header
          class="p-5 pb-2 xl:pb-6 order-1"
          :class="{ 'sm:min-h-[5.5rem] !pb-4': pageType === 'upgrade-plans' }"
        >
          <div class="flex items-center gap-x-1.5">
            <h3
              v-text="plan.label"
              class="text-2xl leading-9 font-semibold"
            />

            <span
              v-if="plan.value === billingStore.bestValuePlan && pageType === 'account-creation'"
              class="font-marker bg-[#F9DC00] px-1 rotate-[-4deg] -mt-1 uppercase"
            >
              Best value
            </span>
          </div>

          <p
            v-text="plan.brief"
            class="text-gray-600"
            :class="{ 'min-h-[3rem] max-lg:min-h-[4.5rem] max-[840px]:min-h-[2.5rem] max-sm:min-h-0': pageType === 'upgrade-plans' }"
          />
        </header>

        <div
          class="p-5 pb-4 h-full cursor-pointer order-2 max-xl:order-3"
          :class="{'!pb-6': pageType === 'upgrade-plans'}"
        >
          <div v-if="pageType === 'upgrade-plans'" class="flex items-center gap-x-2">
            <NumberFlow
              :value="plan.price[billingStore.getPlansPeriods.selected]"
              :format="{ trailingZeroDisplay: 'stripIfInteger' }"
              :prefix="'$'"
              class="text-[40px] leading-none font-semibold flex-none"
            />

            <p class="text-xs leading-none text-gray-600">
              Per month,<br> billed {{ billingStore.getPlansPeriods.selected === 'monthly' ? 'monthly' : 'annually' }}
            </p>
          </div>

          <button
            v-if="pageType === 'upgrade-plans'"
            @click="handleUpgrade(plan)"

            class="py-3.5 mt-[6px] mb-6 w-full text-sm justify-center px-1 primary-button"
            :class="{
              'disabled !pointer-events-none !cursor-default': billingStore.currentPlan?.id === plan.id && configStore.config?.subscription?.annual !== billingStore.getPlansPeriods.selectedIndex
            }"
          >
            <LoadingSpinner v-if="!billingStore.currentPlan?.id" :width="'19'" :height="'19'" :background="'transparent'" />
            <template v-else>
              <span v-if="billingStore.currentPlan.id > 4 || (configStore.config?.subscription?.annual === billingStore.getPlansPeriods.selectedIndex && billingStore.currentPlan.id === plan.id)">Change to {{ plan.label }}</span>
              <template v-else>
                <span v-if="billingStore.currentPlan.id < plan.id">Upgrade to {{ plan.label }}</span>
                <span v-if="billingStore.currentPlan.id > plan.id">Downgrade to {{ plan.label }}</span>
                <span v-if="billingStore.currentPlan.id === plan.id">Your current plan</span>
              </template>
            </template>
          </button>

          <div class="flex flex-col gap-y-2 text-xs">
            <p
              v-if="plan.features.title"
              v-text="plan.features.title"
              class="font-bold"
            />

            <ul class="flex flex-col gap-y-1">
              <li
                v-for="(feature, index) in plan.features.options"
                :key="index"
                class="flex items-start gap-x-1.5"
              >
                <Icon icon="heroicons:check-circle" class="text-lg text-gray-400" />

                <div>
                  <p v-text="feature.label" class="text-gray-600 font-medium" />

                  <div
                    v-if="feature.value === 'listing'"
                    class="flex items-center gap-x-1 2xl:gap-x-2"
                  >
                    <img src="@/assets/images/logos/collx-mini.svg" alt="CollX channel logo">
                    
                    <span>+</span>
                    
                    <div class="flex items-center gap-x-0.5 2xl:gap-x-1">
                      <div class="flex items-center gap-x-0.5 2xl:gap-x-1">
                        <img src="@/assets/images/logos/ebay-mini.svg" alt="CollX channel logo">
                        <img src="@/assets/images/logos/shopify-mini.svg" alt="CollX channel logo">
                        <img src="@/assets/images/logos/whatnot-mini-white.svg" alt="CollX channel logo">
                      </div>

                      <span v-if="plan.value === 'prospect'">(pick 1)</span>
                      <span v-if="plan.value === 'majors'">(pick 2)</span>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <footer
          class="xl:py-6 px-4 xl:border-t order-3 max-xl:order-2 flex flex-col"
          v-if="pageType === 'account-creation'"
        >
          <h5
            v-if="plan.value === billingStore.bestValuePlan"
            v-text="'Recommended for your collection size'"
            class="text-[10px] leading-none font-semibold text-[#057A55] mb-3 max-xl:order-2"
          />

          <div class="flex flex-col gap-y-2 mb-3 xl:mb-4 max-xl:order-1">
            <div class="flex items-center gap-x-2">
              <NumberFlow
                v-if="pageType === 'account-creation'"
                :value="plan.price[billingStore.getPlansPeriods.selected]"
                :format="{ style: 'currency', currency: 'USD', trailingZeroDisplay: 'stripIfInteger' }"
                class="text-[40px] leading-none font-semibold flex-none"
              />

              <Transition
                :enter-active-class="plan.value !== 'prospect' ? 'transition-all duration-300 ease-in' : ''"
                :leave-active-class="plan.value !== 'prospect' ? 'transition-all duration-200 ease-out' : ''"
                :enter-from-class="plan.value !== 'prospect' ? 'opacity-0 -translate-y-2' : ''"
                :enter-to-class="plan.value !== 'prospect' ? 'opacity-1 translate-y-0' : ''"
                :leave-from-class="plan.value !== 'prospect' ? 'opacity-1 translate-y-0' : ''"
                :leave-to-class="plan.value !== 'prospect' ? 'opacity-0 -translate-y-2' : ''"
                mode="out-in"
              >
                <span
                  v-if="plan.value === 'prospect' || billingStore.getPlansPeriods.selected === 'annual'"
                  :key="plan.price.badge.value"
                  v-text="plan.price.badge.value"
                  class="border py-1 px-2 rounded-full text-[10px] leading-3 font-bold"
                  :class="[
                    plan.price.badge.type === 'trial' && 'bg-green-100 border-green-500 text-green-800',
                    plan.price.badge.type === 'save' && 'bg-blue-100 border-blue-600 text-blue-800',
                  ]"
                />
              </Transition>
            </div>

            <p class="text-xs leading-none text-gray-600" v-if="pageType === 'account-creation'">
              Per month, billed {{ billingStore.getPlansPeriods.selected === 'monthly' ? 'monthly' : 'annually' }}
            </p>
          </div>

          <button
            v-if="pageType === 'account-creation'"
            @click="handleContinue(plan)"
            v-text="plan.action_text"
            class="py-3.5 w-full text-sm justify-center px-1 max-xl:order-3 primary-button"
          />
        </footer>
      </div>
    </section>
  </section>
</template>